// hooks/useLogin.ts
import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSmsLogin } from "./useSmsLogin";
import {
  ssoLogin,
  loginOrCreateUserBySSO,
  emailOtpLogin,
  sendBeginEmailOtp,
} from "../../_redux/authCrud";
import { SCHOOL_URL } from "../../../../crud/helpers/routes";
import { handleResponse } from "../../../../crud/helpers/validate";
import qs from "qs";

export const useLogin = (props) => {
  const { intl, authActions, setSelectedSchool } = props;
  const [loading, setLoading] = useState(false);
  const [wValidationCode, setWValidationCode] = useState(false);
  const [notSSO, setNotSSO] = useState(true);
  const [otpEmail, setOtpEmail] = useState(true);
  const [enabled, setEnabled] = useState(false);

  const [loginMethod, setLoginMethod] = useState(undefined);

  const { sendSMSOtp, verifySmsOTP } = useSmsLogin({
    authActions,
    setSelectedSchool,
    setWValidationCode,
    setLoading,
  });

  const initialValues = {
    email: "",
    validationCode: "",
    phoneNumber: "",
  };

  // Manejo de login por SSO desde hash
  useEffect(() => {
    // Pruebas
    const hash = "?" + props.location.hash.substring(1);
    const authParams = qs.parse(hash, { ignoreQueryPrefix: true });

    // Hash
    // let authParams;
    //
    // if (props.location.search && props.location.search.includes("?")) {
    //   authParams = qs.parse(props.location.search, { ignoreQueryPrefix: true });
    // } else if (props.location.hash && props.location.hash.includes("#")) {
    //   const hash = "?" + props.location.hash.substring(1);
    //   authParams = qs.parse(hash, { ignoreQueryPrefix: true });
    // }

    if (
      authParams.access_token &&
      typeof authParams.access_token === "string"
    ) {
      loginOrCreateUserBySSO(authParams.access_token).then((data) => {
        if (data.user.is_admin) {
          setSchool(setSelectedSchool, data.authToken);
        } else if (data.user.school) {
          setSelectedSchool(data.user.school);
        }
        authActions.login(data);
      });
    }
  }, [authActions, props.location.hash, setSelectedSchool]);

  const enableLoading = () => setLoading(true);
  const disableLoading = () => setLoading(false);

  const setSchool = (setterFunction, token) => {
    return fetch(`${SCHOOL_URL}/suggestions?q=`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => handleResponse(res))
      .then((json) => setterFunction(json[0]))
      .catch((e) => console.error(e.errors));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object().shape({
      email: Yup.lazy(() =>
        formik?.getFieldProps("phoneNumber").value === ""
          ? Yup.string()
              .email("Wrong email format")
              .required(
                intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })
              )
          : Yup.string().nullable()
      ),
      phoneNumber: Yup.lazy(() =>
        formik?.getFieldProps("email").value === ""
          ? Yup.string()
              .min(10, "Must be at least 10 digits")
              .matches(/^[0-9]+$/, "Wrong phone format")
              .required(
                intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" })
              )
          : Yup.string().nullable()
      ),
      validationCode: Yup.string()
        .length(6, "Must be 6 digits")
        .required(
          wValidationCode &&
            intl.formatMessage({
              id: "AUTH.VALIDATION.REQUIRED_FIELD",
            })
        ),
    }),
    onSubmit: (values, { setStatus, setSubmitting }) => {
      enableLoading();
      if (otpEmail) {
        window.localStorage.setItem("loginMethod", "email");
        setLoginMethod("email");
        if (wValidationCode) {
          emailOtpLogin(values.email, values.validationCode)
            .then((data) => {
              disableLoading();
              if (data.user.is_admin)
                setSchool(setSelectedSchool, data.authToken);
              else if (data.user.school) setSelectedSchool(data.user.school);
              authActions.login(data);
            })
            .catch(() => {
              disableLoading();
              setSubmitting(false);
              setStatus(
                intl.formatMessage({ id: "AUTH.VALIDATION.INVALID_LOGIN" })
              );
            });
        } else {
          ssoLogin(values.email)
            .then(() => {
              setWValidationCode(false);
              disableLoading();
            })
            .catch(() => {
              sendBeginEmailOtp(values.email)
                .then(({ status }) => {
                  if (status === 200) setWValidationCode(true);
                  setEnabled(true);
                  setStatus(null);
                })
                .catch(() => {
                  disableLoading();
                  setSubmitting(false);
                  setStatus(
                    "Contact not recognized. Please use a valid staff email or contact info@itsthezone.com."
                  );
                })
                .finally(() => {
                  setNotSSO(true);
                  setSubmitting(false);
                  disableLoading();
                });
            });
        }
      } else {
        window.localStorage.setItem("loginMethod", "sms");
        setLoginMethod("sms");
        if (wValidationCode) {
          verifySmsOTP(values.validationCode);
        } else {
          sendSMSOtp(`+${values.phoneNumber}`);
        }
      }
    },
  });

  const getInputClasses = (fieldname) => {
    if (
      (fieldname === "email" && formik.getFieldProps("phoneNumber").value) ||
      (fieldname === "phoneNumber" && formik.getFieldProps("email").value)
    ) {
      return "";
    }
    if (formik.touched[fieldname] && formik.errors[fieldname])
      return "is-invalid";
    if (formik.touched[fieldname] && !formik.errors[fieldname])
      return "is-valid";
    return "";
  };

  const handleChangeLoginMethods = () => {
    setWValidationCode(false);
    formik.values.email = "";
    formik.values.phoneNumber = "";
    formik.touched["email"] = false;
    formik.touched["phoneNumber"] = false;
    setLoginMethod(undefined);
  };

  const isInvalidCombination = useMemo(() => {
    const email = formik.values.email;
    const phone = formik.values.phoneNumber;
    return (email && phone) || (!email && !phone);
  }, [formik.values.email, formik.values.phoneNumber]);

  useEffect(() => {
    setOtpEmail(!!formik.values.email);
  }, [formik.values.email]);

  return {
    loading,
    wValidationCode,
    setWValidationCode,
    notSSO,
    otpEmail,
    enabled,
    formik,
    getInputClasses,
    isInvalidCombination,
    loginMethod,
    setLoginMethod,
    handleChangeLoginMethods,
  };
};
